'use client'

import React from 'react'
import type { ContentBoxesBlock as ContentBoxesBlockProps, Media, Testimonial } from '@/payload-types'
import type { CardWithOrder } from './config'
import { TextContent } from './components/TextContent'
import { MediaContent } from './components/MediaContent'
import { TestimonialContent } from './components/TestimonialContent'
import { KeyPointsContent } from './components/KeyPointsContent'
import { cn } from '@/utilities/ui'
import { getBlockPadding } from '@/utilities/getBlockPadding'

// Type guards for better type safety
function isMediaObject(media: ContentBoxesBlockProps['cards'][0]['media']): media is Media {
  return typeof media === 'object' && media !== null && 'url' in media
}

function isTestimonialObject(testimonial: ContentBoxesBlockProps['cards'][0]['testimonial']): testimonial is Testimonial {
  return typeof testimonial === 'object' && testimonial !== null && 'name' in testimonial
}

type Props = ContentBoxesBlockProps & {
  disableInnerContainer?: boolean
}

export const LargeCardsBlock: React.FC<Props> = ({
  cards,
  disableInnerContainer: _disableInnerContainer,
  paddingTop,
  paddingBottom,
}) => {
  // Get padding classes based on layout options
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })
  if (!cards || cards.length !== 2) {
    return null
  }

  // Type alias for cleaner code
  type Card = NonNullable<ContentBoxesBlockProps['cards']>[number]

  // Helper function to calculate card ordering
  const calculateCardOrder = (cards: Card[]): CardWithOrder[] => {
    const mediaCardIndex = cards.findIndex((card: Card) => card.contentType === 'media')
    const hasMediaCard = mediaCardIndex !== -1

    return cards.map((card: Card, index: number) => {
      let mobileOrder: number
      let desktopOrder: number

      if (hasMediaCard) {
        // Media card first on mobile, preserve desktop order
        if (card.contentType === 'media') {
          mobileOrder = 1
          desktopOrder = index + 1
        } else {
          mobileOrder = 2
          desktopOrder = index + 1
        }
      } else {
        // No media card, preserve original order
        mobileOrder = index + 1
        desktopOrder = index + 1
      }

      const flexOrder = `order-${mobileOrder} lg:order-${desktopOrder}`

      return {
        card,
        orderInfo: {
          mobileOrder,
          desktopOrder,
          flexOrder
        }
      }
    })
  }

  const cardsWithOrder = calculateCardOrder(cards)

  const renderCardContent = (card: NonNullable<typeof cards>[number], className?: string) => {
    switch (card.contentType) {
      case 'text':
        if (!card.richText) return null
        return (
          <TextContent
            richText={card.richText}
            textBackground={card.textBackground || undefined}
            enableCta={card.enableCta || false}
            cta={card.cta || undefined}
            ctaLabel={(card as { ctaLabel?: string }).ctaLabel || undefined}
            className={className}
          />
        )

      case 'media': {
        const mediaType = card.mediaType || 'image'
        const hasBackgroundImage = card.backgroundImage && isMediaObject(card.backgroundImage)
        const hasForegroundImage = card.media && isMediaObject(card.media)

        // Check we have the required media for the selected mode
        if (mediaType === 'backgroundImage' && !hasBackgroundImage) return null
        if (mediaType === 'image' && !hasForegroundImage) return null

        return (
          <MediaContent
            mediaType={mediaType}
            media={hasForegroundImage ? card.media as Media : undefined}
            backgroundImage={hasBackgroundImage ? card.backgroundImage as Media : undefined}
            mediaBackground={card.mediaBackground || undefined}
            imagePosition={card.imagePosition || undefined}
            className={className}
          />
        )
      }

      case 'testimonial':
        if (!isTestimonialObject(card.testimonial)) return null
        return <TestimonialContent testimonial={card.testimonial} className={className} />

      case 'keyPoints':
        if (!card.keyPoints || card.keyPoints.length === 0) return null
        return (
          <KeyPointsContent
            keyPoints={card.keyPoints.map((kp) => ({
              title: kp.title || '',
              subtitle: kp.subtitle || '',
              text: kp.text || '',
            }))}
            className={className}
          />
        )

      default:
        return null
    }
  }

  return (
    <div className={cn("box-border flex flex-col gap-7 lg:gap-[62px] items-start justify-start relative w-full", paddingClasses)}>
      <div className="flex flex-col gap-5 lg:gap-6 items-start justify-start w-full">
        {/* Two-card layout with flexbox ordering */}
        <div className="flex flex-col lg:flex-row gap-5 lg:gap-6 items-stretch w-full">
          {cardsWithOrder.map(({ card, orderInfo }, index) => (
            <div
              key={index}
              className={`flex-1 ${orderInfo.flexOrder}`}
            >
              {renderCardContent(card, 'h-full')}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}